/**
 * scope.ts — the modal live-eval scope builders.
 *
 * `buildNodeScope` mirrors compute.ts's internal node scope (self.*, nodes.*,
 * edges.*) so the NodeModal's ExprField shows real values as the user types.
 * Shared by the live App wiring and the read-only replay modals — replay passes
 * the RECORDED effective params as the overlay so the scope reflects the values
 * that actually ran (agent deltas included), not today's authored defaults.
 */
import type { ScenarioBundle, TickPayload } from '../engine/types';

/** Read a param value through the optional effective-params overlay (the
 *  setParam path grammar: `<nodeId>.<key>` / `e:<edgeId>.<key>`). */
function param(
  effective: Record<string, number> | undefined,
  path: string,
  fallback: number,
): number {
  const v = effective?.[path];
  return typeof v === 'number' ? v : fallback;
}

export function buildNodeScope(
  nodeId: string,
  bundle: ScenarioBundle,
  tick: TickPayload,
  effective?: Record<string, number>,
): Record<string, unknown> {
  const node = bundle.graph.nodes.find((n) => n.id === nodeId);
  const v = tick.nodeValues[nodeId];
  const self: Record<string, number> = {
    stock: v?.stock ?? 0,
    inflow: v?.inflow ?? 0,
    outflow: v?.outflow ?? 0,
  };
  for (const p of node?.params ?? []) self[p.key] = param(effective, `${nodeId}.${p.key}`, p.value);
  for (const p of node?.gate?.params ?? []) self[p.key] = param(effective, `${nodeId}.${p.key}`, p.value);
  const nodesScope: Record<string, unknown> = {};
  for (const n of bundle.graph.nodes) {
    const nv = tick.nodeValues[n.id];
    const np: Record<string, number> = { stock: nv?.stock ?? 0 };
    for (const p of n.params) np[p.key] = param(effective, `${n.id}.${p.key}`, p.value);
    if (nv?.state) for (const [k, val] of Object.entries(nv.state)) np[k] = val;
    nodesScope[n.id] = np;
  }
  const edgesScope: Record<string, unknown> = {};
  for (const e of bundle.graph.edges) {
    edgesScope[e.id] = { flow: tick.edgeFlows[e.id] ?? 0 };
    for (const p of e.params)
      (edgesScope[e.id] as Record<string, number>)[p.key] = param(effective, `e:${e.id}.${p.key}`, p.value);
  }
  return { self, nodes: nodesScope, edges: edgesScope };
}
